import { Subscription } from '../types/subscription';
import { SUBSCRIPTION_PLAN_IDS } from '../types/subscription';
import { subscriptionService } from './subscriptionService';
import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from './firestore';
import { TrialExtensionUtils, TRIAL_EXTENSION_CONSTANTS } from '../constants/trialExtension';

export interface SubscriptionStatus {
  status: 'active' | 'trial' | 'expired' | 'cancelled' | 'suspended';
  isExpired: boolean;
  needsRenewal: boolean;
  shouldDowngrade: boolean;
  message: string;
}

export interface SubscriptionStatusCheckResult {
  originalSubscription: Subscription;
  updatedSubscription: Subscription | null;
  status: SubscriptionStatus;
  wasUpdated: boolean;
  showTrialExpiredModal: boolean;
  error?: string;
}

class SubscriptionStatusService {
  /**
   * Comprehensive subscription status checker
   * Analyzes startDate, endDate, and trialEndDate to determine current status
   */
  async checkAndUpdateSubscriptionStatus(
    userId: string, 
    subscription: Subscription
  ): Promise<SubscriptionStatusCheckResult> {
    try {
      console.log('🔍 SubscriptionStatusService: Starting comprehensive status check');
      console.log('🔍 Original subscription:', subscription);

      const now = Date.now();
      const startDate = parseInt(subscription.startDate);
      const endDate = parseInt(subscription.endDate);
      const trialEndDate = subscription.endDate ? parseInt(subscription.endDate) : null;

      console.log('🔍 Date analysis:', {
        now: new Date(now).toISOString(),
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
        trialEndDate: trialEndDate ? new Date(trialEndDate).toISOString() : 'null'
      });

      console.log('🔍 Subscription details:', {
        id: subscription.id,
        userId: subscription.userId,
        planId: subscription.planId,
        status: subscription.status,
        startDate: subscription.startDate,
        endDate: subscription.endDate
      });

      // Determine subscription status based on date combinations
      const status = this.analyzeSubscriptionStatus(subscription, now, startDate, endDate, trialEndDate || endDate);
      
      console.log('🔍 Analyzed status:', status);

      // If subscription needs updating, update it
      let updatedSubscription: Subscription | null = null;
      let wasUpdated = false;
      let showTrialExpiredModal = false;

      if (status.shouldDowngrade || status.isExpired || status.needsRenewal) {
        console.log('🔧 Subscription needs updating, applying changes...');
        updatedSubscription = await this.updateSubscriptionBasedOnStatus(subscription, status);
        wasUpdated = true;
        console.log('✅ Subscription updated successfully');
      }

      // Check if we should show trial expired modal
      // Show modal if trial has expired (0 or negative days left) and user was on professional plan
      const trialDaysRemaining = this.getTrialDaysRemaining(subscription);
      console.log('🔍 Trial days remaining:', trialDaysRemaining);
      console.log('🔍 Subscription plan ID:', subscription.planId);
      console.log('🔍 Is in trial period:', subscription.status === 'trial');
      
      // Only show modal if trial has expired and user was on professional plan
      // AND user is still in trial period (not already downgraded)
      if (trialDaysRemaining <= 0 && 
          subscription.planId === SUBSCRIPTION_PLAN_IDS.PROFESSIONAL && 
          subscription.status === 'trial') {
        showTrialExpiredModal = true;
        console.log('🔔 Trial expired (0 or negative days left) - automatically downgrading to free plan and showing modal');
        
        // Automatically downgrade to free plan when trial expires
        console.log('🔧 Auto-downgrading expired trial to free plan');
        updatedSubscription = await this.updateSubscriptionBasedOnStatus(subscription, {
          status: 'active', // Set to active for free plan
          isExpired: false, // Not expired, just downgraded
          needsRenewal: false,
          shouldDowngrade: true,
          message: 'Trial expired - auto-downgrading to free plan'
        });
        wasUpdated = true;
        console.log('✅ Subscription automatically downgraded to free plan');
      } else {
        console.log('🔍 Trial status check:', {
          trialDaysRemaining,
          planId: subscription.planId,
          shouldShowModal: false
        });
      }

      return {
        originalSubscription: subscription,
        updatedSubscription,
        status,
        wasUpdated,
        showTrialExpiredModal,
        error: undefined
      };

    } catch (error) {
      console.error('❌ Error checking subscription status:', error);
      return {
        originalSubscription: subscription,
        updatedSubscription: null,
        status: {
          status: 'active',
          isExpired: false,
          needsRenewal: false,
          shouldDowngrade: false,
          message: 'Error checking status'
        },
        wasUpdated: false,
        showTrialExpiredModal: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Analyze subscription status based on date combinations
   */
  private analyzeSubscriptionStatus(
    subscription: Subscription,
    now: number,
    startDate: number,
    endDate: number,
    trialEndDate: number
  ): SubscriptionStatus {
    const isTrialPlan = subscription.planId === SUBSCRIPTION_PLAN_IDS.PROFESSIONAL && subscription.status === 'trial';
    const isFreePlan = subscription.planId === SUBSCRIPTION_PLAN_IDS.FREE;
    const isPaidPlan = subscription.planId === SUBSCRIPTION_PLAN_IDS.BASIC || subscription.planId === SUBSCRIPTION_PLAN_IDS.PROFESSIONAL;

    // Case 1: Free Plan - Always active
    if (isFreePlan) {
      return {
        status: 'active',
        isExpired: false,
        needsRenewal: false,
        shouldDowngrade: false,
        message: 'Free plan - always active'
      };
    }

    // Case 2: Trial Plan Analysis
    if (isTrialPlan && trialEndDate) {
      const trialDaysRemaining = Math.ceil((trialEndDate - now) / (1000 * 60 * 60 * 24));
      
      if (trialDaysRemaining > 0) {
        // Trial is still active
        return {
          status: 'trial',
          isExpired: false,
          needsRenewal: false,
          shouldDowngrade: false,
          message: `Trial active - ${trialDaysRemaining} days remaining`
        };
      } else {
        // Trial has expired - check if there's a renewal
        if (endDate > now) {
          // There's a paid subscription after trial
          return {
            status: 'active',
            isExpired: false,
            needsRenewal: false,
            shouldDowngrade: false,
            message: 'Trial expired, but paid subscription is active'
          };
        } else {
          // Trial expired and no paid subscription - downgrade to free
          return {
            status: 'expired',
            isExpired: true,
            needsRenewal: false,
            shouldDowngrade: true,
            message: 'Trial expired, no paid subscription - downgrading to free plan'
          };
        }
      }
    }

    // Case 3: Trial Plan without trialEndDate - use centralized trial calculation
    if (isTrialPlan && !trialEndDate) {
      // Use the centralized trial calculation logic
      const { TrialExtensionUtils } = require('../constants/trialExtension');
      const registrationDate = new Date(startDate);
      const calculatedTrialEndDate = TrialExtensionUtils.getTrialEndDate(registrationDate);
      const trialDaysRemaining = Math.ceil((calculatedTrialEndDate.getTime() - now) / (1000 * 60 * 60 * 24));
      
      if (trialDaysRemaining > 0) {
        return {
          status: 'trial',
          isExpired: false,
          needsRenewal: false,
          shouldDowngrade: false,
          message: `Trial active (calculated) - ${trialDaysRemaining} days remaining`
        };
      } else {
        // Trial expired - check if there's a renewal
        if (endDate > now) {
          return {
            status: 'active',
            isExpired: false,
            needsRenewal: false,
            shouldDowngrade: false,
            message: 'Trial expired (calculated), but paid subscription is active'
          };
        } else {
          return {
            status: 'expired',
            isExpired: true,
            needsRenewal: false,
            shouldDowngrade: true,
            message: 'Trial expired (calculated), no paid subscription - downgrading to free plan'
          };
        }
      }
    }

    // Case 4: Paid Plan Analysis
    if (isPaidPlan && subscription.status !== 'trial') {
      if (endDate > now) {
        // Subscription is active
        return {
          status: 'active',
          isExpired: false,
          needsRenewal: false,
          shouldDowngrade: false,
          message: 'Paid subscription is active'
        };
      } else {
        // Subscription has expired
        return {
          status: 'expired',
          isExpired: true,
          needsRenewal: true,
          shouldDowngrade: true,
          message: 'Paid subscription has expired - downgrading to free plan'
        };
      }
    }

    // Case 5: Cancelled subscription
    if (subscription.status === 'cancelled') {
      return {
        status: 'cancelled',
        isExpired: true,
        needsRenewal: false,
        shouldDowngrade: false,
        message: 'Subscription is cancelled'
      };
    }

    // Default case - treat as active
    return {
      status: 'active',
      isExpired: false,
      needsRenewal: false,
      shouldDowngrade: false,
      message: 'Default status - treating as active'
    };
  }

  /**
   * Update subscription based on status analysis
   */
  private async updateSubscriptionBasedOnStatus(
    subscription: Subscription,
    status: SubscriptionStatus
  ): Promise<Subscription> {
    const now = Date.now();
    const updates: Partial<Subscription> = {
      updatedAt: now.toString()
    };

    if (status.shouldDowngrade) {
      // Downgrade to free plan
      updates.planId = SUBSCRIPTION_PLAN_IDS.FREE;
      updates.status = 'active';
      updates.totalPrice = 0;
      updates.currency = 'EGP';
      
      // Set new end date (1 month from now for free plan)
      // Use centralized paid subscription end date calculation
      const { TrialExtensionUtils } = require('../constants/trialExtension');
      const endDate = TrialExtensionUtils.getPaidSubscriptionEndDate(new Date(now), 'monthly');
      updates.endDate = endDate.getTime().toString();
      
      console.log('🔧 Downgrading to free plan with updates:', updates);
    }

    if (status.isExpired) {
      console.log('🔧 Marking subscription as expired');
    }

    // Update subscription in Firestore using new path structure
    const userSubscriptionRef = doc(db, 'Subscriptions', subscription.userId);
    
    // Create update object
    const firestoreUpdates: any = { ...updates };
    
    console.log('🔧 Updating subscription document at:', userSubscriptionRef.path);
    console.log('🔧 Update data:', firestoreUpdates);
    
    await updateDoc(userSubscriptionRef, firestoreUpdates);
    console.log('✅ Updated subscription in Subscriptions collection');

    return {
      ...subscription,
      ...updates
    };
  }

  /**
   * Handle trial expiration by downgrading to free plan
   */
  async handleTrialExpiration(userId: string, subscription: Subscription): Promise<Subscription> {
    console.log('🔧 Handling trial expiration - downgrading to free plan');
    
    const now = Date.now();
    const updates: Partial<Subscription> = {
      planId: SUBSCRIPTION_PLAN_IDS.FREE,
      status: 'active',
      totalPrice: 0,
      currency: 'EGP',
      updatedAt: now.toString(),
      // Set new end date (1 month from now for free plan)
      endDate: TrialExtensionUtils.getPaidSubscriptionEndDate(new Date(now), 'monthly').getTime().toString()
    };

    // Update subscription in Firestore using new path structure
    const userSubscriptionRef = doc(db, 'Subscriptions', userId);
    
    // Create update object
    const firestoreUpdates: any = { ...updates };
    
    console.log('🔧 Updating subscription document at:', userSubscriptionRef.path);
    console.log('🔧 Update data:', firestoreUpdates);
    
    await updateDoc(userSubscriptionRef, firestoreUpdates);
    console.log('✅ Updated subscription in Subscriptions collection');

    return {
      ...subscription,
      ...updates
    };
  }

  /**
   * Get trial days remaining with support for extended trials
   */
  getTrialDaysRemaining(subscription: Subscription): number {
    if (subscription.status !== 'trial') {
      return 0;
    }

    const now = new Date();
    const startDate = new Date(parseInt(subscription.startDate));
    
    // Check if this is an extended trial based on trialExtensionReason
    const isExtendedTrial = subscription.trialExtensionReason === 'business_extension' || 
      (subscription.endDate && 
       parseInt(subscription.endDate) === TRIAL_EXTENSION_CONSTANTS.EXTENDED_TRIAL_END_DATE.getTime());
    
    if (isExtendedTrial) {
      // Use extended trial calculation
      return TrialExtensionUtils.getExtendedTrialDaysRemaining(now);
    } else {
      // Use standard trial calculation
      return TrialExtensionUtils.getStandardTrialDaysRemaining(startDate, now);
    }
  }
}

// Export singleton instance
export const subscriptionStatusService = new SubscriptionStatusService();
export default subscriptionStatusService;
